"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, readFragment } from "@opensea/graphql";
import { isAddressEqual, UnreachableCaseError } from "@opensea/utils";
import { cloneDeep, merge, pick, set } from "es-toolkit/compat";
import { omit } from "es-toolkit/object";
import { ProfileItemsCardFragment } from "@/components/items/profile/ProfileItemsGrid";
import { useProfileItemsSelectionStore } from "@/components/items/profile/ProfileItemsSelectionProvider";
import { ProfileItemsTableRowFragment } from "@/components/items/profile/ProfileItemsTable";
import { profileItemsSelectionFragment } from "@/components/items/profile/profile-items-selection-store";
import { getIsItemOwnedBy, ItemOwnedQuantityFragment } from "@/components/items/utils";
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment";
import { isNewer } from "@/lib/live-updates";
import { Paginator } from "@/lib/pagination/paginator";
import { useSubscription } from "../use-subscription";
import { profileItemPaginatorFragment } from "./use-profile-items-subscription.fragment";
const fungibleItemSubscriptions = graphql(`
    subscription useFungibleProfileItemsSubscription(
      $address: Address!
      $identifiers: [ItemIdentifierInput!]!
    ) {
      itemsByIdentifiers(identifiers: $identifiers) {
        id
        collection {
          slug
        }
        enforcement {
          isDelisted
        }
        ...ProfileItemPaginator
        ...ProfileItemsTableRowFragment
        ...ProfileItemsCardFragment
        ...profileItemsSelection
        version
      }
    }
  `, [ProfileItemsTableRowFragment, ProfileItemsCardFragment, profileItemsSelectionFragment, profileItemPaginatorFragment]);
const subscription = graphql(`
    subscription useProfileItemsSubscription(
      $address: Address!
      $filter: ProfileItemsFilter
    ) {
      profileItem(address: $address, filter: $filter) {
        id
        collection {
          id
          slug
        }
        lastTransferAt
        ownership(address: $address) {
          id
          isHidden
        }
        ...ItemOwnedQuantity
        enforcement {
          isDelisted
        }
        isFungible
        chain {
          identifier
        }
        contractAddress
        tokenId
        version
        bestListing {
          startTime
          maker {
            address
          }
        }
        bestOffer {
          pricePerItem {
            usd
          }
        }
        lowestListingForOwner(address: $address) {
          pricePerItem {
            usd
          }
        }
        ...ProfileItemPaginator
        ...ProfileItemsTableRowFragment
        ...ProfileItemsCardFragment
        ...profileItemsSelection
      }
    }
  `, [ProfileItemsTableRowFragment, ProfileItemsCardFragment, profileItemsSelectionFragment, profileItemPaginatorFragment, ItemOwnedQuantityFragment]);
const profileCollectionsSubscription = graphql(`
    subscription useProfileCollectionsSubscription($slugs: [String!]!) {
      collectionsBySlugs(slugs: $slugs) {
        __typename
        ... on Collection {
          id
          slug
          floorPrice {
            pricePerItem {
              ...TokenPrice
            }
          }
          topOffer {
            pricePerItem {
              native {
                unit
              }
              ...TokenPrice
            }
            maker {
              address
            }
          }
        }
      }
    }
  `, [TokenPriceFragment]);
class ProfileItemPaginator extends Paginator {
  getValues(key, sortBy) {
    const item = readFragment(profileItemPaginatorFragment, key);
    switch (sortBy) {
      case "LISTING_OWN_PRICE":
        return [item.lowestListingForOwner ? item.lowestListingForOwner.pricePerItem.usd : undefined, item.id];
      case "RECEIVED_DATE":
        return [item.lastTransferAt ? new Date(item.lastTransferAt) : undefined, item.id];
      case "RARITY":
        return [item.rarity?.rank || undefined, item.id];
      case "TOP_OFFER":
        return [Math.max(item.collection?.topOffer?.pricePerItem.usd ?? 0, item.bestOffer?.pricePerItem.usd ?? 0), item.id];
      case "FLOOR_PRICE":
        return [item.collection?.floorPrice?.pricePerItem.usd ?? 0, item.id];
      case "COST":
        return [item.lastSale?.usd, item.id];
      case "LISTED_DATE":
        return [item.bestListing?.startTime || undefined, item.id];
      case "CREATED_DATE":
        return [item.createdAt || undefined, item.id];
      case "LAST_SALE_PRICE":
        return [item.lastSale?.usd || undefined, item.id];
      case undefined:
        return [item.id];
      default:
        throw new UnreachableCaseError(sortBy);
    }
  }
  getSortDirection(sort) {
    switch (sort?.by) {
      case "RARITY":
        return this.inverseDirection(sort.direction);
      default:
        return sort?.direction;
    }
  }
}
const profileItemPaginator = new ProfileItemPaginator({
  applyIf: isNewer
});
export function useProfileItemsSubscription(t0) {
  const $ = _c(32);
  const {
    address,
    fungibleItemIdentifiers: t1,
    sortBy,
    sortDirection,
    setItems,
    pagination,
    collectionSlugs,
    filter
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const fungibleItemIdentifiers = t2;
  let t3;
  if ($[2] !== filter) {
    t3 = filter ? omit(filter, ["isHidden"]) : filter;
    $[2] = filter;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const subscriptionFilter = t3;
  const variables = {
    address: address ?? "",
    filter: subscriptionFilter
  };
  const updateItemSelections = useProfileItemsSelectionStore(_temp);
  let t4;
  if ($[4] !== sortBy || $[5] !== sortDirection) {
    t4 = {
      by: sortBy,
      direction: sortDirection
    };
    $[4] = sortBy;
    $[5] = sortDirection;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const sort = t4;
  let t5;
  if ($[7] !== filter?.isHidden || $[8] !== pagination || $[9] !== setItems || $[10] !== sort || $[11] !== updateItemSelections) {
    t5 = data => {
      const item = data.profileItem;
      setItems(prev => {
        if (item.enforcement.isDelisted || !getIsItemOwnedBy(item) || !filter?.isHidden && item.ownership?.isHidden || filter?.isHidden && !item.ownership?.isHidden) {
          return profileItemPaginator.remove(item, prev, sort);
        }
        return profileItemPaginator.insertInOrder(item, prev, sort, {
          insertIntoLastPlace: !pagination.hasNext
        });
      });
      updateItemSelections();
    };
    $[7] = filter?.isHidden;
    $[8] = pagination;
    $[9] = setItems;
    $[10] = sort;
    $[11] = updateItemSelections;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useSubscription({
    query: subscription,
    variables,
    pause: !address,
    resetKey: `${JSON.stringify(variables)}-${sortBy}-${sortDirection}`
  }, t5);
  const t6 = address ?? "";
  let t7;
  if ($[13] !== fungibleItemIdentifiers || $[14] !== t6) {
    t7 = {
      address: t6,
      identifiers: fungibleItemIdentifiers
    };
    $[13] = fungibleItemIdentifiers;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = !address || fungibleItemIdentifiers.length === 0;
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = {
      query: fungibleItemSubscriptions,
      variables: t7,
      pause: t8
    };
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== address || $[20] !== setItems || $[21] !== sort) {
    t10 = msg => {
      setItems(prev_0 => {
        const update = msg.itemsByIdentifiers;
        return profileItemPaginator.updateInOrder(prev_0, sort, item_0 => item_0.id === update.id, item_1 => {
          const updates = ["bestListing", "bestItemOffer"];
          if (!item_1.bestListing || isAddressEqual(item_1.bestListing.maker.address, address)) {
            updates.push("lowestListingForOwner");
          }
          return cloneDeep(merge(item_1, pick(update, updates)));
        });
      });
    };
    $[19] = address;
    $[20] = setItems;
    $[21] = sort;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  useSubscription(t9, t10);
  let t11;
  if ($[23] !== collectionSlugs) {
    t11 = {
      slugs: collectionSlugs
    };
    $[23] = collectionSlugs;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = !collectionSlugs.length;
  let t13;
  if ($[25] !== t11 || $[26] !== t12) {
    t13 = {
      query: profileCollectionsSubscription,
      variables: t11,
      pause: t12
    };
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== setItems || $[29] !== sortBy || $[30] !== sortDirection) {
    t14 = data_0 => {
      const collection = data_0.collectionsBySlugs;
      if (collection.__typename === "Collection") {
        const floor = collection.floorPrice;
        const topOffer = collection.topOffer;
        setItems(prev_1 => {
          const updatedItems = prev_1.filter(item_2 => item_2.collection?.slug === collection.slug);
          return updatedItems.reduce((acc, item_3) => profileItemPaginator.updateInOrder(acc, {
            by: sortBy,
            direction: sortDirection
          }, element => element.id === item_3.id, element_0 => {
            set(element_0, "collection.floorPrice", floor);
            set(element_0, "collection.topOffer", topOffer);
            return element_0;
          }), prev_1);
        });
      }
    };
    $[28] = setItems;
    $[29] = sortBy;
    $[30] = sortDirection;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  useSubscription(t13, t14);
}
function _temp(store) {
  return store.updateItemSelections;
}